import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { AppContext } from "../../appContext";
import { Spinner } from "../../components/spinner/Spinner";
import { AnalysisProvider } from "../../api/fileProvider/analysisProvider";
import { MultiFileProvider } from "../../api/fileProvider/multiFileProvider";
import { IFileProvider } from "../../api/fileProvider/IFileProvider";
import { IAnalysisFolder } from "../dashboardPage/components/fullAnalysis/IAnalysisFolder";
import { FullAnalysisFolderList } from "../dashboardPage/components/fullAnalysis/FullAnalysisFolderList";
import { FullAnalysisCrumbs } from "../dashboardPage/components/fullAnalysis/FullAnalysisCrumbs";
import { FullAnalysisFile } from "../dashboardPage/components/fullAnalysis/FullAnalysisFile";
import { getFolders } from "../../api/folderApi";
import { IPlanarFolder } from "../dashboardPage/components/fullAnalysis/FSPlanarize";
import "./FolderDeepLinkPage.css";

interface FolderPathItem {
    id: string;
    name: string;
}

const buildFolderPath = (folderId: string, allFolders: IPlanarFolder[]): FolderPathItem[] => {
    const path: FolderPathItem[] = [];
    let currentId: string | undefined = folderId;
    
    while (currentId) {
        const folder = allFolders.find(f => f.id === currentId);
        if (!folder) break;
        path.unshift({ id: folder.id || '', name: folder.name });
        currentId = folder.parent;
    }
    
    return path;
};

export const FolderDeepLinkPage = () => {
    const { folderId } = useParams<{ folderId: string }>();
    const { translate, user } = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fileProvider, setFileProvider] = useState<IFileProvider | null>(null);
    const [fix, setFix] = useState(0);
    const [navigationPath, setNavigationPath] = useState<FolderPathItem[]>([]);
    const [currentPathIndex, setCurrentPathIndex] = useState(-1);
    const navigationStarted = useRef(false);

    console.log('[FolderDeepLinkPage] Render, folderId:', folderId, 'user:', user?.uid || 'null', 'pathIndex:', currentPathIndex, 'pathLength:', navigationPath.length);

    useEffect(() => {
        console.log('[FolderDeepLinkPage] useEffect[folderId], folderId:', folderId, 'user:', user?.uid || 'null');
        
        if (!folderId) {
            console.log('[FolderDeepLinkPage] No folder ID provided');
            setError("No folder ID provided");
            setLoading(false);
            return;
        }

        if (!user) {
            console.log('[FolderDeepLinkPage] Waiting for user auth...');
            return;
        }

        if (navigationStarted.current) {
            return;
        }
        navigationStarted.current = true;

        console.log('[FolderDeepLinkPage] Creating MultiFileProvider and loading path');
        
        const initNavigation = async () => {
            try {
                console.log('[FolderDeepLinkPage] Calling getFolders()...');
                const response = await getFolders();
                console.log('[FolderDeepLinkPage] getFolders response status:', response.status);
                
                const foldersData: Array<Record<string, IPlanarFolder>> = await response.json();
                console.log('[FolderDeepLinkPage] foldersData:', foldersData);
                
                if (!foldersData || !Array.isArray(foldersData)) {
                    console.log('[FolderDeepLinkPage] Invalid folders data');
                    setError("Failed to load folders");
                    setLoading(false);
                    return;
                }
                
                const allFolders: IPlanarFolder[] = foldersData.map((it: Record<string, IPlanarFolder>) => {
                    const id = Object.keys(it)[0];
                    return { ...it[id], id };
                });

                const targetFolder = allFolders.find(f => f.id === folderId);
                if (!targetFolder) {
                    console.log('[FolderDeepLinkPage] Target folder not found:', folderId);
                    setError("Folder not found");
                    setLoading(false);
                    return;
                }

                const path = buildFolderPath(folderId, allFolders);
                console.log('[FolderDeepLinkPage] Navigation path:', path);
                setNavigationPath(path);

                const provider = new MultiFileProvider(AnalysisProvider);
                provider.onChange = () => {
                    console.log('[FolderDeepLinkPage] Provider onChange triggered');
                    setFix(last => last + 1);
                };
                provider.onAuthError = () => {
                    console.log('[FolderDeepLinkPage] Auth error from provider');
                    setError("Authentication error");
                };
                setFileProvider(provider);
                
                console.log('[FolderDeepLinkPage] Starting root load');
                await provider.getFiles();
                setCurrentPathIndex(0);
                
            } catch (err) {
                console.error("[FolderDeepLinkPage] Error initializing:", err);
                setError("Error loading folder");
                setLoading(false);
            }
        };

        initNavigation();
    }, [folderId, user]);

    useEffect(() => {
        if (!fileProvider || navigationPath.length === 0 || currentPathIndex < 0) {
            return;
        }

        const crumbs = fileProvider.crumbs || [];
        const currentFolder = crumbs[crumbs.length - 1];
        
        console.log('[FolderDeepLinkPage] Navigation step check, pathIndex:', currentPathIndex, 'crumbs:', crumbs.length, 'currentFolder:', currentFolder?.id);

        if (currentPathIndex >= navigationPath.length) {
            console.log('[FolderDeepLinkPage] Navigation complete!');
            setLoading(false);
            return;
        }

        const targetItem = navigationPath[currentPathIndex];
        console.log('[FolderDeepLinkPage] Target item:', targetItem.id, targetItem.name);

        if (!currentFolder || !currentFolder.folderItems) {
            console.log('[FolderDeepLinkPage] Waiting for folder items to load...');
            return;
        }

        const foundInItems = currentFolder.folderItems.find(item => item.id === targetItem.id);
        
        if (foundInItems) {
            console.log('[FolderDeepLinkPage] Found target in folderItems, navigating to:', targetItem.id);
            fileProvider.getFiles(targetItem.id, targetItem.name);
            setCurrentPathIndex(prev => prev + 1);
        } else {
            const alreadyInCrumbs = crumbs.find(c => c.id === targetItem.id);
            if (alreadyInCrumbs) {
                console.log('[FolderDeepLinkPage] Already at target level, moving to next');
                setCurrentPathIndex(prev => prev + 1);
            } else {
                console.log('[FolderDeepLinkPage] Target not found in current level, items:', currentFolder.folderItems?.map(i => i.id));
            }
        }
    }, [fileProvider, navigationPath, currentPathIndex, fix]);

    const crumbs: IAnalysisFolder[] = fileProvider?.crumbs || [];
    const currentFolder = crumbs[crumbs.length - 1];

    const handleCrumb = (folder: IAnalysisFolder) => {
        fileProvider?.getFiles(folder.id, folder.name);
    };

    const handleSelect = (folder: IAnalysisFolder) => {
        fileProvider?.getFiles(folder.id);
    };

    if (loading) {
        return (
            <div className="FolderDeepLinkPage">
                <div className="FolderDeepLinkPage_loading">
                    <Spinner />
                    <span>{translate('loading') || 'Loading...'}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="FolderDeepLinkPage">
                <div className="FolderDeepLinkPage_error">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="FolderDeepLinkPage">
            <div className="FolderDeepLinkPage_header">
                {crumbs.length > 0 && (
                    <FullAnalysisCrumbs
                        crumbs={crumbs}
                        onCrumb={handleCrumb}
                    />
                )}
            </div>
            <div className="FolderDeepLinkPage_content">
                {currentFolder?.type === 'folder' && (
                    <FullAnalysisFolderList
                        folder={currentFolder}
                        onSelect={handleSelect}
                        onRename={() => {}}
                        onDelete={() => {}}
                        onSettings={() => {}}
                    />
                )}
                {currentFolder?.type === 'file' && (
                    <FullAnalysisFile file={currentFolder} />
                )}
            </div>
        </div>
    );
};
